import React, { useMemo } from 'react'
import { useLazyLoadQuery, useFragment, graphql } from 'react-relay'
import {
  useParams,
  Routes,
  Route,
  useNavigate,
  useMatch,
} from 'react-router-dom'

import { lazy as loadable } from '@loadable/component'

import { UserMetadata } from '@components'
import { YStack, XStack } from 'tamagui'

import { H2, Button, wrapSuspenseComponent, Link } from '@newComponents'
import { Group } from '@newComponents/Group'

import { UserEventsScreenQuery } from './__generated__/UserEventsScreenQuery.graphql'

const UserEventsUpcomingScreen = wrapSuspenseComponent(
  loadable(() => import('../UserEventsUpcomingScreen/UserEventsUpcomingScreen'))
)

const UserEventsPastScreen = wrapSuspenseComponent(
  loadable(() => import('../UserEventsPastScreen/UserEventsPastScreen'))
)

export const UserEventsScreen = () => {
  const params = useParams()

  const upcomingMatch = useMatch(`${params.actorId}/events/upcoming`)
  const pastMatch = useMatch(`${params.actorId}/events/past`)

  const data = useLazyLoadQuery<UserEventsScreenQuery>(
    graphql`
      query UserEventsScreenQuery($id: String!) {
        nodeBySlugId(id: $id) {
          __typename

          ... on User {
            firstName
            lastName

            ...UserMetadata_user
            ...UserEventsScreenPastEventsSection_user
            ...UserEventsScreenUpcomingEventsSection_user
          }
        }
      }
    `,
    { id: params.actorId || '' }
  )

  const upcomingOrPast = useMemo(() => {
    if (upcomingMatch) {
      return 'upcoming'
    }

    if (pastMatch) {
      return 'past'
    }

    return 'upcoming'
  }, [upcomingMatch, pastMatch])

  if (data.nodeBySlugId?.__typename !== 'User') {
    return null
  }

  return (
    <>
      <UserMetadata
        user={data.nodeBySlugId}
        title={`${data.nodeBySlugId.firstName} ${
          data.nodeBySlugId.lastName
        }'s ${upcomingOrPast === 'upcoming' ? 'Upcoming' : 'Past'} Events`}
      />

      <YStack gap='$6' flex={1}>
        <XStack alignItems='center' gap='$4'>
          <XStack flex={1}>
            <H2>
              {upcomingOrPast === 'upcoming' ? 'Upcoming' : 'Past'} Events
            </H2>
          </XStack>

          <Group activeIndex={upcomingOrPast === 'past' ? 1 : 0}>
            <XStack gap='$2'>
              <Group.Item index={0}>
                <Link
                  href={`/${params.actorId}/events/upcoming`}
                  size='$4'
                  fontWeight='$6'
                  color='$grey9'
                  hoverStyle={{
                    textDecorationLine: 'none',
                  }}
                  paddingVertical='$2'
                  paddingHorizontal='$3'
                >
                  Upcoming
                </Link>
              </Group.Item>

              <Group.Item index={1}>
                <Link
                  href={`/${params.actorId}/events/past`}
                  size='$4'
                  fontWeight='$6'
                  color='$grey9'
                  hoverStyle={{
                    textDecorationLine: 'none',
                  }}
                  paddingVertical='$2'
                  paddingHorizontal='$3'
                >
                  Past
                </Link>
              </Group.Item>
            </XStack>
          </Group>
        </XStack>

        <Routes>
          <Route index element={<UserEventsUpcomingScreen />} />

          <Route path='upcoming' element={<UserEventsUpcomingScreen />} />

          <Route path='past' element={<UserEventsPastScreen />} />
        </Routes>
      </YStack>
    </>
  )
}

export default UserEventsScreen
